import { useTranslation } from "react-i18next"
import { Button } from "@/components/ui/button"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { SortableTableHead } from "@/components/ui/sortable-table-head"
import { Pencil, Trash2 } from "lucide-react"
import type { Location, LocationFilters } from "@/types/location"

interface LocationTableProps {
  data: Location[]
  filters: LocationFilters
  onSort: (column: string) => void
  onEdit: (item: Location) => void
  onDelete: (item: Location) => void
}

export function LocationTable({ data, filters, onSort, onEdit, onDelete }: LocationTableProps) {
  const { t } = useTranslation()

  return (
    <div className="overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <SortableTableHead
              label={t("locations.nameLabel")}
              sortKey="name"
              currentSortKey={filters.sort}
              direction={filters.direction}
              onSort={onSort}
            />
            <TableHead className="hidden md:table-cell">{t("locations.streetLabel")}</TableHead>
            <SortableTableHead
              label={t("locations.cityStateLabel")}
              sortKey="city"
              currentSortKey={filters.sort}
              direction={filters.direction}
              onSort={onSort}
              className="hidden lg:table-cell"
            />
            <TableHead className="w-[100px]">{t("common.actions")}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {data.map((item, index) => (
            <TableRow key={item.id} className={index % 2 === 1 ? "bg-muted" : ""}>
              <TableCell className="font-medium">{item.name}</TableCell>
              <TableCell className="hidden md:table-cell">{item.street || "-"}</TableCell>
              <TableCell className="hidden lg:table-cell">
                {item.city && item.state ? `${item.city} - ${item.state}` : item.city || item.state || "-"}
              </TableCell>
              <TableCell>
                <div className="flex items-center gap-1">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => onEdit(item)}
                    title={t("locations.editLocation")}
                  >
                    <Pencil className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => onDelete(item)}
                    className="text-destructive hover:text-destructive"
                    title={t("locations.deleteLocation")}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  )
}
